'use strict';

import * as React from 'react';
import { Button } from 'react-bootstrap';
import type { UserApiToken } from '../../MedicareProAPI/Entities/User/UserApiToken';
import { UserApiTokenRepository } from '../../MedicareProAPI/Repositories/User/UserApiTokenRepository';
import StyledModal from '../Components/StyledModal';

interface UserApiTokensPanelProps {
  userAccessToken: string;
  userId: number;
}

/** Maximum number of API tokens a user can have */
const MAX_TOKENS = 10;

/**
 * Panel for managing user API tokens.
 *
 * Displays a list of API tokens with the ability to revoke (delete) them.
 * Includes a modal for generating new tokens with copy functionality.
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2026-01-21
 * @version 1.0.0
 */
export const UserApiTokensPanel = ({
  userAccessToken,
  userId,
}: UserApiTokensPanelProps): React.JSX.Element => {
  const [tokens, setTokens] = React.useState<UserApiToken[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [newTokenName, setNewTokenName] = React.useState<string>('');
  const [newTokenExpiry, setNewTokenExpiry] = React.useState<string>('');
  const [createdToken, setCreatedToken] = React.useState<UserApiToken | null>(null);
  const [creating, setCreating] = React.useState<boolean>(false);
  const [copied, setCopied] = React.useState<boolean>(false);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const apiTokenRepository = React.useMemo(() => {
    const r = new UserApiTokenRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  /**
   * Create a new API token.
   */
  const handleCreateToken = async (): Promise<void> => {
    if (!newTokenName.trim()) return;

    setCreating(true);
    try {
      const payload: { name: string; expiresAt?: string } = {
        name: newTokenName.trim(),
      };
      if (newTokenExpiry) {
        payload.expiresAt = new Date(newTokenExpiry).toISOString();
      }
      const token = await apiTokenRepository.createUserApiToken(userId, payload);
      setCreatedToken(token);
      setTokens((prev) => [token, ...prev]);
    } catch (error) {
      console.error('Failed to create API token:', error);
    } finally {
      setCreating(false);
    }
  };

  /**
   * Delete (revoke) an API token.
   */
  const handleDeleteToken = async (tokenId: number): Promise<void> => {
    if (!confirm('Are you sure you want to revoke this API token? This action cannot be undone.')) {
      return;
    }

    setDeletingId(tokenId);
    try {
      await apiTokenRepository.deleteUserApiToken(userId, tokenId);
      setTokens((prev) => prev.filter((t) => t.getId() !== tokenId));
    } catch (error) {
      console.error('Failed to delete API token:', error);
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * Copy the token to clipboard.
   */
  const handleCopyToken = async (): Promise<void> => {
    if (!createdToken) return;

    try {
      await navigator.clipboard.writeText(createdToken.getToken());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy token:', error);
    }
  };

  /**
   * Close the create modal and reset state.
   */
  const handleCloseModal = (): void => {
    setShowCreateModal(false);
    setNewTokenName('');
    setNewTokenExpiry('');
    setCreatedToken(null);
    setCopied(false);
  };

  /**
   * Open the create modal.
   */
  const handleOpenModal = (): void => {
    setShowCreateModal(true);
    setNewTokenName('');
    setNewTokenExpiry('');
    setCreatedToken(null);
    setCopied(false);
  };

  /**
   * Format a date string for display.
   */
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Get the status badge for a token.
   */
  const getTokenStatus = (token: UserApiToken): React.JSX.Element => {
    if (token.isExpired()) {
      return <span className="badge badge-danger ml-2">Expired</span>;
    }
    const expiresAt = token.getExpiresAt();
    if (expiresAt) {
      const daysUntilExpiry = Math.ceil(
        (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      if (daysUntilExpiry <= 7) {
        return <span className="badge badge-warning ml-2">Expires in {daysUntilExpiry}d</span>;
      }
    }
    return null;
  };

  // Fetch tokens on mount
  React.useEffect(() => {
    const fetchTokens = async (): Promise<void> => {
      setLoading(true);
      try {
        const collection = await apiTokenRepository.getUserApiTokens(userId);
        setTokens(collection.getItems());
      } catch (error) {
        console.error('Failed to fetch API tokens:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchTokens();
  }, [apiTokenRepository, userId]);

  const isAtLimit = tokens.length >= MAX_TOKENS;

  return (
    <div className="col-12 px-2" id="api-tokens">
      <div className="card h-auto">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>API Tokens</span>
          <Button
            variant="primary"
            size="sm"
            onClick={handleOpenModal}
            disabled={isAtLimit}
            title={isAtLimit ? `Maximum of ${MAX_TOKENS} tokens reached` : ''}
          >
            <i className="fas fa-plus mr-1"></i> Generate New Token
          </Button>
        </div>
        <div className="card-body">
          {isAtLimit && (
            <div className="alert alert-warning py-1 mb-2" style={{ fontSize: '10pt' }}>
              <i className="fas fa-exclamation-triangle mr-2"></i>
              You have reached the maximum of {MAX_TOKENS} API tokens. Revoke an existing token to
              create a new one.
            </div>
          )}
          <div className="alert alert-info py-1" style={{ fontSize: '10pt' }}>
            <i className="far fa-info-circle mr-2"></i>
            API tokens allow third-party applications to access your account. Keep your tokens
            secure and revoke any that are no longer needed.
            <div className="mt-2">
              <i className="far fa-book mr-2"></i>Learn more about API Token by visiting our{' '}
              <a
                target="_blank"
                href="https://help.medicareproapp.com/en/articles/10143297"
                rel="noreferrer"
              >
                Help Guides
              </a>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <i className="fal fa-spinner fa-spin fa-2x"></i>
              <p className="mt-2 text-muted">Loading tokens...</p>
            </div>
          ) : tokens.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="fal fa-key fa-2x mb-2"></i>
              <p>No API tokens found. Generate one to get started.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Last Used</th>
                    <th>Expires</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => (
                    <tr key={token.getId()} className={token.isExpired() ? 'table-secondary' : ''}>
                      <td>
                        <i className="fas fa-key text-muted mr-2"></i>
                        <b>{token.getName()}</b>
                        {getTokenStatus(token)}
                      </td>
                      <td>
                        <span className={!token.getLastUsedAt() ? 'text-muted' : ''}>
                          {formatDate(token.getLastUsedAt())}
                        </span>
                      </td>
                      <td>
                        <span className={!token.getExpiresAt() ? 'text-muted' : ''}>
                          {token.getExpiresAt() ? formatDate(token.getExpiresAt()) : 'Never'}
                        </span>
                      </td>
                      <td className="text-right">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteToken(token.getId())}
                          disabled={deletingId === token.getId()}
                        >
                          {deletingId === token.getId() ? (
                            <i className="fal fa-spinner fa-spin"></i>
                          ) : (
                            <>
                              <i className="fas fa-trash-alt mr-1"></i> Revoke
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Token Modal */}
      <StyledModal
        size="md"
        title="Generate New API Token"
        show={showCreateModal}
        onClose={handleCloseModal}
        footerButtons={
          !createdToken ? (
            <>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateToken}
                disabled={creating || !newTokenName.trim()}
              >
                {creating ? (
                  <>
                    <i className="fal fa-spinner fa-spin mr-1"></i> Generating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus mr-1"></i> Generate Token
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={handleCloseModal}>
              Done
            </Button>
          )
        }
      >
        {!createdToken ? (
          <div>
            <div className="form-group">
              <label htmlFor="tokenName">Token Name</label>
              <input
                type="text"
                className="form-control"
                id="tokenName"
                placeholder="e.g., My Integration"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                autoFocus
              />
              <small className="form-text text-muted">
                Give your token a descriptive name so you can identify it later.
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="tokenExpiry">Expiration Date (Optional)</label>
              <input
                type="date"
                className="form-control"
                id="tokenExpiry"
                value={newTokenExpiry}
                onChange={(e) => setNewTokenExpiry(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <small className="form-text text-muted">
                Leave blank for a token that never expires. Setting an expiry is recommended for
                security.
              </small>
            </div>
          </div>
        ) : (
          <div>
            <div className="alert alert-success" style={{ fontSize: '10pt' }}>
              <i className="fas fa-check-circle mr-2"></i>
              Token generated successfully!
            </div>

            <div className="alert alert-warning" style={{ fontSize: '10pt' }}>
              <i className="fas fa-exclamation-triangle mr-2"></i>
              <strong>Important:</strong> Copy your token now. You will not be able to see it again!
            </div>

            <div className="form-group">
              <label>Your API Token</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control font-monospace"
                  value={createdToken.getToken()}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
                <div className="input-group-append">
                  <Button
                    variant={copied ? 'success' : 'outline-secondary'}
                    onClick={handleCopyToken}
                  >
                    {copied ? (
                      <>
                        <i className="fas fa-check mr-1"></i> Copied!
                      </>
                    ) : (
                      <>
                        <i className="fas fa-copy mr-1"></i> Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </StyledModal>
    </div>
  );
};
